import { useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  EmploymentType,
  PersonnelClassification,
  PersonnelStatus,
} from '@dsr/shared-types';
import {
  usePersonnelList,
  useCreatePersonnel,
  type PersonnelFilters,
} from '../api/queries/personnel';
import { DateInput } from '../components/DateInput';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import { toast } from '../components/Toasts';
import {
  Badge,
  Button,
  Dialog,
  FormField,
  FormRow,
  Input,
  Select,
  Table,
  type BadgeTone,
  type SelectOption,
} from '../components/ui';

// ---------- Enum option tables (label maps mirror the API CHECK enums) ----------

const STATUS_OPTIONS: { value: PersonnelStatus; label: string }[] = [
  { value: 'applicant', label: 'Applicant' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'terminated', label: 'Terminated' },
];

const CLASSIFICATION_OPTIONS: { value: PersonnelClassification; label: string }[] = [
  { value: 'unarmed_guard', label: 'Unarmed Guard' },
  { value: 'armed_guard', label: 'Armed Guard' },
  { value: 'ep_agent', label: 'EP Agent' },
  { value: 'detail_leader', label: 'Detail Leader' },
  { value: 'dispatcher', label: 'Dispatcher' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'driver', label: 'Driver' },
  { value: 'medic', label: 'Medic' },
];

const EMPLOYMENT_TYPE_OPTIONS: { value: EmploymentType; label: string }[] = [
  { value: 'full_time', label: 'Full-time' },
  { value: 'part_time', label: 'Part-time' },
  { value: 'contractor', label: 'Contractor' },
  { value: 'temp', label: 'Temporary' },
];

const STATUS_LABEL = Object.fromEntries(
  STATUS_OPTIONS.map((o) => [o.value, o.label]),
) as Record<PersonnelStatus, string>;

const CLASSIFICATION_LABEL = Object.fromEntries(
  CLASSIFICATION_OPTIONS.map((o) => [o.value, o.label]),
) as Record<PersonnelClassification, string>;

export function statusTone(status: PersonnelStatus): string {
  switch (status) {
    case 'active':
      return 'success';
    case 'applicant':
    case 'inactive':
      return 'default';
    case 'suspended':
      return 'warn';
    case 'terminated':
      return 'danger';
    default:
      return 'default';
  }
}

// statusTone()'s legacy class suffix → shared Badge tone (colors preserved).
const STATUS_BADGE_TONE: Record<PersonnelStatus, BadgeTone> = {
  active: 'success',
  applicant: 'neutral',
  inactive: 'neutral',
  suspended: 'warn',
  terminated: 'danger',
};

export function PersonnelPage(): JSX.Element {
  const navigate = useNavigate();
  const canWrite = can('personnel.write');

  const [status, setStatus] = useState<PersonnelStatus | 'all'>('all');
  const [classification, setClassification] = useState<PersonnelClassification | 'all'>('all');
  const [q, setQ] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const filters: PersonnelFilters = useMemo(() => {
    const f: PersonnelFilters = {};
    if (status !== 'all') f.status = status;
    if (classification !== 'all') f.classification = classification;
    if (q.trim()) f.q = q.trim();
    return f;
  }, [status, classification, q]);

  const { data, isLoading, isError, error } = usePersonnelList(filters);
  const people = data?.personnel ?? [];

  const statusFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All statuses' },
    ...STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
  ];
  const classificationFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All classifications' },
    ...CLASSIFICATION_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
  ];

  return (
    <section className="page personnel-page">
      <header className="page-header">
        <div>
          <h1>Personnel</h1>
          <p className="page-sub">Guards, agents, and staff — profiles and credentials.</p>
        </div>
        {canWrite && (
          <div className="page-toolbar">
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              New Personnel
            </Button>
          </div>
        )}
      </header>

      <div className="filters-bar">
        <label className="form-field">
          <span>Status</span>
          <Select
            value={status}
            onValueChange={(v) => setStatus(v as PersonnelStatus | 'all')}
            options={statusFilterOptions}
            aria-label="Filter by status"
          />
        </label>

        <label className="form-field">
          <span>Classification</span>
          <Select
            value={classification}
            onValueChange={(v) => setClassification(v as PersonnelClassification | 'all')}
            options={classificationFilterOptions}
            aria-label="Filter by classification"
          />
        </label>

        <label className="form-field filter-search">
          <span>Search</span>
          <input
            type="search"
            className="form-input"
            placeholder="Name or employee #"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
      </div>

      {isLoading && <div className="page-state">Loading personnel…</div>}
      {isError && (
        <div className="page-state page-error" role="alert">
          {error instanceof ApiError ? error.message : 'Failed to load personnel.'}
        </div>
      )}

      {!isLoading && !isError && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Name</Table.HeaderCell>
              <Table.HeaderCell>Employee #</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Classifications</Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {people.length === 0 ? (
              <Table.Empty colSpan={4}>
                No personnel match the current filters.
              </Table.Empty>
            ) : (
              people.map((p) => (
                <Table.Row
                  key={p.id}
                  interactive
                  onClick={() => navigate(`/personnel/${p.id}`)}
                  tabIndex={0}
                  role="link"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') navigate(`/personnel/${p.id}`);
                  }}
                >
                  <Table.Cell tone="strong">{p.legal_name}</Table.Cell>
                  <Table.Cell tone="mono">{p.employee_no ?? '—'}</Table.Cell>
                  <Table.Cell>
                    <Badge tone={STATUS_BADGE_TONE[p.status]}>
                      {STATUS_LABEL[p.status] ?? p.status}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell tone="muted">
                    {p.classifications.length === 0
                      ? '—'
                      : p.classifications
                          .map((c) => CLASSIFICATION_LABEL[c] ?? c)
                          .join(', ')}
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table>
      )}

      {createOpen && (
        <CreatePersonnelModal open={createOpen} onClose={() => setCreateOpen(false)} />
      )}
    </section>
  );
}

// =============================================================================
// Create personnel modal
// =============================================================================

interface CreateModalProps {
  open: boolean;
  onClose: () => void;
}

function CreatePersonnelModal({ open, onClose }: CreateModalProps): JSX.Element {
  const create = useCreatePersonnel();

  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [employeeNo, setEmployeeNo] = useState('');
  const [status, setStatus] = useState<PersonnelStatus>('applicant');
  const [classifications, setClassifications] = useState<PersonnelClassification[]>([]);
  // HR fields (0021)
  const [positionTitle, setPositionTitle] = useState('');
  const [department, setDepartment] = useState('');
  const [employmentType, setEmploymentType] = useState<EmploymentType>('full_time');
  const [hireDate, setHireDate] = useState<number | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const toggleClassification = (c: PersonnelClassification): void => {
    setClassifications((prev) =>
      prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c],
    );
  };

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setFormError(null);
    if (!firstName.trim() || !lastName.trim()) {
      setFormError('First and last name are required.');
      return;
    }
    create.mutate(
      {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        status,
        ...(middleName.trim() ? { middle_name: middleName.trim() } : {}),
        ...(employeeNo.trim() ? { employee_no: employeeNo.trim() } : {}),
        ...(classifications.length > 0 ? { classifications } : {}),
        ...(positionTitle.trim() ? { position_title: positionTitle.trim() } : {}),
        ...(department.trim() ? { department: department.trim() } : {}),
        employment_type: employmentType,
        ...(hireDate != null ? { hire_date: hireDate } : {}),
      },
      {
        onSuccess: () => {
          toast.success('Personnel created.');
          onClose();
        },
        onError: (err) => {
          setFormError(err instanceof ApiError ? err.message : 'Failed to create personnel.');
        },
      },
    );
  };

  return (
    <Dialog
      title="New Personnel"
      open={open}
      onClose={onClose}
      busy={create.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="create-personnel-form"
            variant="primary"
            disabled={create.isPending}
          >
            {create.isPending ? 'Saving…' : 'Create'}
          </Button>
        </>
      }
    >
      <form id="create-personnel-form" onSubmit={onSubmit} className="form-grid">
        <FormRow columns={3}>
          <FormField label="First name" required>
            <Input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              autoFocus
            />
          </FormField>

          <FormField label="Middle name">
            <Input value={middleName} onChange={(e) => setMiddleName(e.target.value)} />
          </FormField>

          <FormField label="Last name" required>
            <Input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
          </FormField>
        </FormRow>

        <FormRow>
          <FormField label="Employee #">
            <Input value={employeeNo} onChange={(e) => setEmployeeNo(e.target.value)} />
          </FormField>

          <FormField label="Status">
            <select
              className="form-input"
              value={status}
              onChange={(e) => setStatus(e.target.value as PersonnelStatus)}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </FormField>
        </FormRow>

        <FormRow>
          <FormField label="Position title">
            <Input value={positionTitle} onChange={(e) => setPositionTitle(e.target.value)} />
          </FormField>

          <FormField label="Department">
            <Input value={department} onChange={(e) => setDepartment(e.target.value)} />
          </FormField>
        </FormRow>

        <FormRow>
          <FormField label="Employment type">
            <select
              className="form-input"
              value={employmentType}
              onChange={(e) => setEmploymentType(e.target.value as EmploymentType)}
            >
              {EMPLOYMENT_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="Hire date">
            <DateInput value={hireDate} onChange={setHireDate} />
          </FormField>
        </FormRow>

        <fieldset className="form-field form-fieldset">
          <legend>Classifications</legend>
          <div className="checkbox-grid">
            {CLASSIFICATION_OPTIONS.map((o) => (
              <label key={o.value} className="checkbox-field">
                <input
                  type="checkbox"
                  checked={classifications.includes(o.value)}
                  onChange={() => toggleClassification(o.value)}
                />
                <span>{o.label}</span>
              </label>
            ))}
          </div>
        </fieldset>

        {formError && (
          <p className="form-error" role="alert">
            {formError}
          </p>
        )}
      </form>
    </Dialog>
  );
}
